/** Passive, bounded browser-only journal. Nothing here writes to chat or run context. */
import { create } from 'zustand';
import type { SseFrame } from '../transport/sse';

export const DEV_TOOLS = import.meta.env.VITE_PH_DEV_TOOLS === '1';
export const MAX_ENTRIES = 200;
export type Category = 'actions' | 'data' | 'events';
export type Entry = { id: number; at: number; threadId: string; runId: string | null; stream: string; ownerThreadId: string; key?: string; category: Category; title: string; data: unknown };
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Bound BEFORE serialization and scrub secrets, including keys embedded in map URLs. */
export function scrub(value: unknown): unknown {
  let nodes = 500;
  let characters = 24000;
  const visit = (v: unknown, depth: number): unknown => {
    if (--nodes < 0 || characters <= 0 || depth > 6) return '[данные сокращены]';
    if (typeof v === 'string') {
      const text = v.slice(0, Math.min(12000, characters)); characters -= text.length;
      return text
        .replace(/([?&](?:key|api[_-]?key|token|access_token|signature)=)[^&#\s"<>]+/gi, '$1[скрыто]')
        .replace(/("(?:api[_-]?key|access_token|token|secret|password|authorization)"\s*:\s*")[^"]*/gi, '$1[скрыто]')
        .replace(/\b(?:glpat-[\w.\-]+|Bearer\s+[\w.\-]+|eyJ[\w-]+\.[\w-]+\.[\w-]+)\b/gi, '[скрыто]');
    }
    if (Array.isArray(v)) return v.slice(0, 80).map((item) => visit(item, depth + 1));
    if (object(v)) return Object.fromEntries(Object.entries(v).slice(0, 100).map(([key, item]) =>
      [key.slice(0, 100), /(?:^|_)(?:token|secret|password|authorization|api[_-]?key|reasoning|thinking)(?:$|_)/i.test(key) ? '[скрыто]' : visit(item, depth + 1)]));
    return typeof v === 'bigint' ? String(v) : v;
  };
  return visit(value, 0);
}

type Journal = { enabled: boolean; entries: Entry[]; setEnabled: (enabled: boolean) => void; clear: () => void };
let pending: Entry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let nextId = 0;
export const useDiagnostics = create<Journal>((set) => ({
  enabled: DEV_TOOLS, entries: [],
  setEnabled: (enabled) => { set({ enabled }); if (!enabled) flushDiagnostics(); },
  clear: () => { pending = []; if (timer) clearTimeout(timer); timer = null; set({ entries: [] }); },
}));
export function flushDiagnostics(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  const batch = pending; pending = [];
  if (batch.length) useDiagnostics.setState((s) => {
    let entries = s.entries;
    for (const entry of batch) {
      if (entry.key) entries = entries.filter((old) => old.key !== entry.key);
      entries = [...entries, entry].slice(-MAX_ENTRIES);
    }
    return { entries };
  });
}
export function recordDiagnostic(identity: { threadId: string; assistantId?: string; runId?: string | null; stream?: string; ownerThreadId?: string }, category: Category, title: string, data: unknown, key?: string): void {
  try {
    if (!DEV_TOOLS || identity.assistantId !== 'peterhof_agent' || !useDiagnostics.getState().enabled) return;
    pending.push({ id: ++nextId, at: Date.now(), threadId: identity.threadId, runId: identity.runId ?? null,
      stream: identity.stream ?? 'main', ownerThreadId: identity.ownerThreadId ?? identity.threadId, key, category, title: String(scrub(title)).slice(0, 200), data: scrub(data) });
    if (key) pending = pending.filter((entry, index) => entry.key !== key || index === pending.length - 1);
    pending = pending.slice(-MAX_ENTRIES);
    if (!timer) timer = setTimeout(flushDiagnostics, 150);
  } catch { /* diagnostics never fails a turn */ }
}

export function observeFrame(frame: SseFrame, identity: { threadId: string; assistantId?: string; runId?: string | null; stream?: string; ownerThreadId?: string }): void {
  try {
    if (!DEV_TOOLS || identity.assistantId !== 'peterhof_agent' || !useDiagnostics.getState().enabled) return;
    const { event, data } = frame;
    const toolMessage = (raw: unknown) => {
      if (!object(raw)) return;
      const calls = raw.tool_calls ?? (object(raw.additional_kwargs) ? raw.additional_kwargs.tool_calls : null);
      if (Array.isArray(calls)) for (const call of calls) {
        if (!object(call)) continue;
        const callId = call.id ?? `${raw.id ?? 'chunk'}:${call.index ?? call.name ?? 'tool'}`;
        recordDiagnostic(identity, 'actions', 'Вызов инструмента', { id: raw.id, call }, `${identity.threadId}:call:${callId}`);
      }
      if (raw.type === 'tool' || raw.role === 'tool') recordDiagnostic(identity, 'data', `Результат: ${raw.name ?? raw.tool_call_id ?? 'инструмент'}`,
        { id: raw.id, tool_call_id: raw.tool_call_id, name: raw.name, content: raw.content }, `${identity.threadId}:result:${raw.tool_call_id ?? raw.id ?? raw.name}`);
    };
    if (event.startsWith('messages') && event !== 'messages/metadata') {
      (Array.isArray(data) ? data : [data]).forEach(toolMessage);
    } else if (event === 'values' && object(data)) {
      const values = object(data.chunk) ? data.chunk : data;
      if (Array.isArray(values.messages)) {
        const messages = values.messages;
        let lastHuman = -1;
        for (let i = messages.length - 1; i >= 0; i--) { const m = messages[i]; if (object(m) && (m.type === 'human' || m.role === 'user')) { lastHuman = i; break; } }
        messages.slice(Math.max(lastHuman + 1, messages.length - 30)).forEach(toolMessage);
      }
      const state = Object.fromEntries(['actions', 'nav', 'board', 'attachments', '__interrupt__'].filter((key) => key in values).map((key) => [key, values[key]]));
      recordDiagnostic(identity, 'data', 'Загруженное состояние', state);
    } else if (event === 'custom' && object(data)) {
      const wrapped = object(data.chunk) ? data.chunk : data;
      const envelope = object(wrapped.dh_ui) ? wrapped.dh_ui : wrapped;
      if (envelope.kind === 'reasoning') return;
      // Route geometry contains the visitor's request-only origin. It belongs
      // only to the transient map pane, never to the diagnostics journal.
      if (envelope.kind === 'navigation' || envelope.kind === 'location_selection') return;
      if (envelope.kind === 'diagnostics' && object(envelope.payload)) {
        const p = envelope.payload;
        const category = p.category === 'actions' || p.category === 'data' ? p.category : 'events';
        recordDiagnostic(identity, category, String(p.title ?? 'Диагностика'), p.data);
        return;
      }
      recordDiagnostic(identity, envelope.kind === 'activity' ? 'actions' : 'events', String(envelope.kind ?? event), envelope);
    } else if (['metadata', 'error', 'end'].includes(event)) {
      recordDiagnostic(identity, 'events', event, data);
    }
  } catch { /* malformed debug data must not change chat consumption */ }
}
